import { useRef, useState, type CSSProperties } from 'react';
import {
  Box,
  DarkMode,
  Flex,
  IconButton,
  Spinner,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import { Avatar } from '../Avatar';
import { Mic, MicOff, Phone, PictureInPicture2, WifiOff } from 'lucide-react';
import { useAtom } from 'jotai';
import { callTileCornerAtom } from '../../store';
import {
  cornerPosition,
  defaultTileCorner,
  isDrag,
  nearestCorner,
} from '../../lib/callTile';
import { useT } from '../../i18n';
import { StreamVideo } from './callMedia';

const TILE_BG = '#211E1B';
const DECLINE = '#C62828';

function HangUpIcon({ size }: { size: number }) {
  return (
    <Phone
      size={size}
      style={{ transform: 'rotate(135deg)' }}
      aria-hidden="true"
    />
  );
}

export interface MiniCallProps {
  /** The chat's name, for labels. */
  title: string;
  clock: string;
  video: boolean;
  main: {
    name: string;
    avatarUrl?: string;
    /** Whose face, and the call's family to open it through (D292). */
    ownerId?: string;
    familyId?: string | null;
    /** Set when their picture is showing (or held in PiP). */
    stream: MediaStream | null;
    /** Camera off: their avatar covers the (kept) picture. */
    pictureOff?: boolean;
    /** Their connection dropped (D37). */
    reconnecting?: boolean;
  } | null;
  /** Your camera, when it's on. */
  localStream: MediaStream | null;
  mirrorLocal: boolean;
  micEnabled: boolean;
  onToggleMic: () => void;
  onEnd: () => void;
}

// The minimised call (M70, D267): a small window over the app with the main
// picture, you in the corner, mute and hang up. Drag it to any corner; tap
// it to go back to the call. Not a dialog: the app underneath keeps focus
// and works as usual.
export function CallTile({
  title,
  clock,
  video,
  main,
  localStream,
  mirrorLocal,
  micEnabled,
  onToggleMic,
  onEnd,
  onExpand,
  inPictureInPicture,
  videoFallback,
}: MiniCallProps & {
  onExpand: () => void;
  /** Weak connection: video paused or better (D225); acted on full screen. */
  videoFallback?: 'paused' | 'better' | null;
  /** Showing in a picture-in-picture window instead (D268). */
  inPictureInPicture: boolean;
}) {
  const t = useT();
  const [dragged, setCorner] = useAtom(callTileCornerAtom);
  const wide = useBreakpointValue({ base: false, md: true }, { ssr: false });
  const corner = dragged ?? defaultTileCorner(wide === true);
  const [offset, setOffset] = useState<{ x: number; y: number } | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const drag = useRef<{ x: number; y: number; moved: boolean } | null>(null);
  const suppressClick = useRef(false);
  const name = title || t('chat.callScreen.yourFamily');

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    drag.current = { x: event.clientX, y: event.clientY, moved: false };
  };
  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const current = drag.current;
    if (!current) return;
    const dx = event.clientX - current.x;
    const dy = event.clientY - current.y;
    if (!current.moved && isDrag(dx, dy)) {
      current.moved = true;
      event.currentTarget.setPointerCapture?.(event.pointerId);
    }
    if (current.moved) setOffset({ x: dx, y: dy });
  };
  const onPointerUp = () => {
    const current = drag.current;
    drag.current = null;
    if (!current?.moved) return;
    const rect = ref.current?.getBoundingClientRect();
    if (rect) {
      const height = window.visualViewport?.height ?? window.innerHeight;
      setCorner(
        nearestCorner(
          rect.left + rect.width / 2,
          rect.top + rect.height / 2,
          window.innerWidth,
          height
        )
      );
    }
    setOffset(null);
    // The click that follows a drag isn't a tap.
    suppressClick.current = true;
    setTimeout(() => {
      suppressClick.current = false;
    }, 0);
  };
  const onPointerCancel = () => {
    drag.current = null;
    setOffset(null);
  };

  const face = main ? (
    <Flex
      h="full"
      direction="column"
      align="center"
      justify="center"
      gap={1}
      // Clear of the mute and hang-up buttons.
      pb="48px"
    >
      <Avatar
        size={{ base: 'lg', md: 'md' }}
        name={main.name}
        src={main.avatarUrl}
        ownerId={main.ownerId}
        familyId={main.familyId}
      />
      <Text
        fontSize="13px"
        fontWeight={700}
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {clock}
      </Text>
    </Flex>
  ) : (
    <Flex h="full" align="center" justify="center" pb="48px">
      <Text fontSize="13px" fontWeight={700}>
        {clock}
      </Text>
    </Flex>
  );

  return (
    <DarkMode>
      <Box
        ref={ref}
        role="region"
        aria-label={t('chat.callScreen.onCallWith', { name })}
        data-theme="dark"
        data-testid="call-tile"
        position="fixed"
        // Above the app's banners, below Chakra's modal overlays (1300) so
        // a confirmation's buttons are never under it.
        zIndex={1250}
        {...cornerPosition(corner)}
        w={{ base: '120px', md: '288px' }}
        h={{ base: '170px', md: '162px' }}
        borderRadius="18px"
        overflow="hidden"
        bg={TILE_BG}
        color="#F3F0EB"
        boxShadow="0 0 0 1px #3A3632, 0 10px 28px rgba(0, 0, 0, 0.45)"
        transform={
          offset ? `translate(${offset.x}px, ${offset.y}px)` : undefined
        }
        cursor={offset ? 'grabbing' : 'grab'}
        sx={{ touchAction: 'none', userSelect: 'none' }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
      >
        <Box
          as="button"
          type="button"
          position="absolute"
          inset={0}
          w="full"
          h="full"
          aria-label={t('chat.callScreen.backTo', { name })}
          onClick={() => {
            if (suppressClick.current) return;
            onExpand();
          }}
          _focusVisible={{ boxShadow: 'inset 0 0 0 3px #F0B63F' }}
          borderRadius="inherit"
        >
          {inPictureInPicture ? (
            <Flex
              h="full"
              direction="column"
              align="center"
              justify="center"
              gap={2}
              px={2}
              textAlign="center"
            >
              <PictureInPicture2 size={24} aria-hidden="true" />
              <Text fontSize="12px" fontWeight={600}>
                {t('chat.callScreen.inPictureInPicture')}
              </Text>
            </Flex>
          ) : video && main?.stream ? (
            <>
              <StreamVideo stream={main.stream} />
              {main.pictureOff && (
                <Box position="absolute" inset={0} bg={TILE_BG}>
                  {face}
                </Box>
              )}
            </>
          ) : (
            face
          )}
        </Box>

        {main?.reconnecting && !inPictureInPicture && (
          <Flex
            position="absolute"
            inset={0}
            bg="rgba(15, 14, 13, 0.6)"
            direction="column"
            align="center"
            justify="center"
            gap={1}
            pb="48px"
            role="status"
            pointerEvents="none"
          >
            <Spinner color="#F0B63F" emptyColor="#3A3632" size="sm" />
            <Text fontSize="11px" fontWeight={600} textAlign="center" px={1}>
              {t('chat.callScreen.reconnecting')}
            </Text>
          </Flex>
        )}

        {videoFallback && (
          <Flex
            position="absolute"
            top="6px"
            left="6px"
            w="26px"
            h="26px"
            borderRadius="full"
            bg="rgba(15, 14, 13, 0.85)"
            align="center"
            justify="center"
            pointerEvents="none"
            role="img"
            aria-label={t(
              videoFallback === 'paused'
                ? 'chat.callScreen.weakVideoPaused'
                : 'chat.callScreen.videoBetter'
            )}
          >
            <WifiOff size={14} color="#F0B63F" aria-hidden="true" />
          </Flex>
        )}

        {video && localStream && !inPictureInPicture && (
          <Box
            position="absolute"
            top="6px"
            right="6px"
            w={{ base: '36px', md: '72px' }}
            h={{ base: '50px', md: '54px' }}
            borderRadius="8px"
            overflow="hidden"
            bg="#4A3A30"
            boxShadow="0 0 0 1.5px #0F0E0D"
            pointerEvents="none"
            aria-hidden="true"
          >
            <StreamVideo stream={localStream} mirror={mirrorLocal} />
          </Box>
        )}

        <Flex
          position="absolute"
          left={0}
          right={0}
          bottom="6px"
          justify="center"
          gap="8px"
        >
          <IconButton
            colorScheme="gray"
            size="sm"
            w="40px"
            h="40px"
            borderRadius="full"
            aria-label={t(
              micEnabled
                ? 'chat.callScreen.muteMic'
                : 'chat.callScreen.unmuteMic'
            )}
            aria-pressed={!micEnabled}
            icon={micEnabled ? <Mic size={18} /> : <MicOff size={18} />}
            bg={micEnabled ? 'rgba(15, 14, 13, 0.75)' : '#F3F0EB'}
            color={micEnabled ? '#F3F0EB' : '#171513'}
            _hover={{ opacity: 0.9 }}
            onClick={onToggleMic}
          />
          <IconButton
            colorScheme="gray"
            size="sm"
            w="40px"
            h="40px"
            borderRadius="full"
            aria-label={t('chat.callScreen.end')}
            icon={<HangUpIcon size={18} />}
            bg={DECLINE}
            color="white"
            _hover={{ bg: '#B02020' }}
            onClick={onEnd}
          />
        </Flex>
      </Box>
    </DarkMode>
  );
}

// Inside the Document Picture-in-Picture window (D268). That window has
// none of the app's styles, so everything here is inline.
const pipButton: CSSProperties = {
  width: 48,
  height: 48,
  borderRadius: 24,
  border: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
};

export function CallPipView({
  title,
  clock,
  main,
  localStream,
  mirrorLocal,
  micEnabled,
  onToggleMic,
  onEnd,
}: Omit<MiniCallProps, 'video'>) {
  const t = useT();
  return (
    <div
      role="region"
      aria-label={t('chat.callScreen.onCallWith', {
        name: title || t('chat.callScreen.yourFamily'),
      })}
      style={{
        position: 'fixed',
        inset: 0,
        background: '#0F0E0D',
        color: '#F3F0EB',
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      {main?.stream ? (
        <StreamVideo stream={main.stream} />
      ) : (
        <div
          style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
          }}
        >
          <strong style={{ fontSize: 18 }}>{main?.name ?? title}</strong>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{clock}</span>
        </div>
      )}
      {localStream && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            right: 10,
            bottom: 76,
            width: 84,
            height: 112,
            borderRadius: 12,
            overflow: 'hidden',
            background: '#4A3A30',
            boxShadow: '0 0 0 2px #0F0E0D',
          }}
        >
          <StreamVideo stream={localStream} mirror={mirrorLocal} />
        </div>
      )}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 14,
          display: 'flex',
          justifyContent: 'center',
          gap: 14,
        }}
      >
        <button
          type="button"
          aria-label={t(
            micEnabled ? 'chat.callScreen.muteMic' : 'chat.callScreen.unmuteMic'
          )}
          aria-pressed={!micEnabled}
          onClick={onToggleMic}
          style={{
            ...pipButton,
            background: micEnabled ? '#2A2724' : '#F3F0EB',
            color: micEnabled ? '#F3F0EB' : '#171513',
          }}
        >
          {micEnabled ? <Mic size={22} /> : <MicOff size={22} />}
        </button>
        <button
          type="button"
          aria-label={t('chat.callScreen.end')}
          onClick={onEnd}
          style={{ ...pipButton, background: DECLINE, color: 'white' }}
        >
          <HangUpIcon size={22} />
        </button>
      </div>
    </div>
  );
}
